(() => {
  /**
   * UG 超级菜单交互
   * - PC：集合 Tab；系列二级展开 + 三级面板
   * - 移动端：整抽屉左右分栏（L1/L2 左栏，内容右栏）
   */
  if (window.__ugMenuInitialized) return;
  window.__ugMenuInitialized = true;

  const isMobile = () => window.innerWidth <= 768;

  let desktopObserverBound = false;
  let openSyncScheduled = false;
  let submenuResizeObserver = null;

  /** 仅操作手机抽屉内的分栏条目（避开桌面 header-menu 同构副本） */
  const getDrawerEntries = () => document.querySelectorAll('.ug-mobile-drawer__menu [data-ug-m-entry]');

  const getEntryPanel = (entry) => {
    if (!(entry instanceof HTMLElement)) return null;
    const entryId = entry.getAttribute('data-ug-m-id');
    const split = entry.closest('.ug-mobile-drawer__split');
    if (entryId && split) {
      const moved = split.querySelector(`[data-ug-m-panel][data-ug-m-id="${entryId}"]`);
      if (moved) return moved;
    }
    return entry.querySelector('[data-ug-m-panel]');
  };

  /** Support L2：按右侧 item 实际数量重建 */
  const ensureSupportL2 = (l2Slot, panel) => {
    const items = panel.querySelectorAll('.ug-support-menu-item');
    if (!items.length) return;

    const existing = l2Slot.querySelectorAll('.ug-m-support-l2');
    const countsMatch = existing.length === items.length;
    const indicesMatch =
      countsMatch &&
      Array.from(items).every((item, idx) => {
        const btn = existing[idx];
        return btn instanceof HTMLElement && btn.dataset.supportIndex === String(idx);
      });

    if (indicesMatch) {
      items.forEach((item, idx) => {
        item.dataset.supportIndex = String(idx);
      });
      return;
    }

    existing.forEach((btn) => btn.remove());
    items.forEach((item, idx) => {
      const titleEl = item.querySelector('.ug-support-menu-item__title');
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ug-m-support-l2';
      btn.textContent = titleEl?.textContent?.trim() || '';
      btn.dataset.supportIndex = String(idx);
      item.dataset.supportIndex = String(idx);
      l2Slot.appendChild(btn);
    });

    // 重建后按右栏 active（或首项）恢复左栏高亮
    let activeIdx = Array.from(items).findIndex((item) => item.classList.contains('is-active'));
    if (activeIdx < 0) activeIdx = 0;
    items.forEach((item, idx) => item.classList.toggle('is-active', idx === activeIdx));
    l2Slot.querySelectorAll('.ug-m-support-l2').forEach((btn, idx) => {
      btn.classList.toggle('is-active', idx === activeIdx);
    });
  };

  /**
   * 将各菜单的 L2 挪到左栏、panel 挪到 panel-space。
   * 幂等：DOM 被还原时会重新校验并搬迁。
   */
  const prepareMobileSplitEntries = () => {
    getDrawerEntries().forEach((entry, index) => {
      if (!(entry instanceof HTMLElement)) return;

      const l2Slot = entry.querySelector('[data-ug-m-l2]');
      if (!l2Slot) return;

      const entryId = entry.getAttribute('data-ug-m-id') || `ug-m-${index}`;
      entry.setAttribute('data-ug-m-id', entryId);

      const panelSpace = entry.closest('.ug-mobile-drawer__split')?.querySelector('.ug-mobile-drawer__panel-space');
      if (!panelSpace) return;

      let panel =
        panelSpace.querySelector(`[data-ug-m-panel][data-ug-m-id="${entryId}"]`) ||
        entry.querySelector('[data-ug-m-panel]');
      if (!panel) return;

      panel.setAttribute('data-ug-m-id', entryId);
      const type = entry.getAttribute('data-ug-m-type') || '';

      if (type === 'mega') {
        const tabList =
          l2Slot.querySelector('.ug-mega-menu-left__menu-tab-list') ||
          panel.querySelector('.ug-mega-menu-left__menu-tab-list');
        if (tabList && tabList.parentElement !== l2Slot) {
          // 桌面副本带 mobile:hidden，挪到手机左栏后必须去掉
          tabList.classList.remove('mobile:hidden');
          l2Slot.appendChild(tabList);
        }

        const seriesNav =
          l2Slot.querySelector('.ug-mega-menu-series__nav') || panel.querySelector('.ug-mega-menu-series__nav');
        if (seriesNav) {
          seriesNav.querySelectorAll('.ug-mega-menu-series-l3').forEach((l3, idx) => {
            const seriesPanel = l3.querySelector('.ug-mega-menu-series-l3__panel');
            if (!seriesPanel) return;
            const key = l3.getAttribute('data-series-panel') || `series-l3-${idx}`;
            l3.setAttribute('data-series-panel', key);
            seriesPanel.setAttribute('data-series-panel-target', key);
            if (seriesPanel.parentElement !== panel) panel.appendChild(seriesPanel);
          });
          if (seriesNav.parentElement !== l2Slot) l2Slot.appendChild(seriesNav);
        }
      }

      if (type === 'support') {
        ensureSupportL2(l2Slot, panel);
      }

      if (panel.parentElement !== panelSpace) panelSpace.appendChild(panel);
    });
  };

  /**
   * @param {HTMLElement} entry
   * @param {boolean} active
   * @param {{ syncPanel?: boolean }} [options] syncPanel=false 时只收起左栏，保留右栏
   */
  const setMobileEntryActive = (entry, active, { syncPanel = true } = {}) => {
    if (!(entry instanceof HTMLElement)) return;
    entry.classList.toggle('is-active', active);
    const l1 = entry.querySelector('[data-ug-m-l1]');
    if (l1 instanceof HTMLElement) l1.setAttribute('aria-expanded', active ? 'true' : 'false');

    const panel = getEntryPanel(entry);
    if (syncPanel) {
      panel?.classList.toggle('is-active', active);
    }
    // 右栏可见性跟 is-active 对齐（收起左栏但保留右栏时 panel 仍可见）
    if (panel instanceof HTMLElement) {
      const panelActive = panel.classList.contains('is-active');
      panel.setAttribute('aria-hidden', panelActive ? 'false' : 'true');
      // 隐藏面板不可聚焦，避免 trapFocus Tab 进不可见链接
      if (panelActive) panel.removeAttribute('inert');
      else panel.setAttribute('inert', '');
    }
  };

  /** iOS 给 overflow 容器写 scrollTop 会让整层闪白，已在顶部则不动 */
  const resetPanelScroll = (panel) => {
    if (!(panel instanceof HTMLElement) || panel.scrollTop === 0) return;
    panel.scrollTop = 0;
  };

  /** 点内容区时 iOS 会把原生 details 点到 summary 上，关闭按钮会闪一下 */
  const keepMobileDrawerOpen = (target) => {
    if (!(target instanceof HTMLDetailsElement)) return false;
    if (target.id !== 'Details-menu-drawer-container') return false;
    if (target.open) return false;
    if (!target.classList.contains('menu-open')) return false;
    target.setAttribute('open', '');
    document.documentElement.setAttribute('scroll-lock', '');
    return true;
  };

  /** 集合 Tab / wrapper 成对同步，避免双 active */
  const syncCollectionDefaults = (entry, panel) => {
    // Tab 可能还在 panel 里（尚未挪到左栏），两处都查
    const tabs = entry.querySelectorAll('.ug-mega-menu-left-category-name');
    const tabsFromPanel = panel?.querySelectorAll('.ug-mega-menu-left-category-name');
    const tabList = tabs.length ? tabs : tabsFromPanel;
    const wrappers = panel?.querySelectorAll('.ug-mega-menu-left-category-list-content-wrapper');
    if (!tabList?.length || !wrappers?.length) return;

    let activeIdx = Array.from(tabList).findIndex((t) => t.classList.contains('active'));
    const wrapperIdx = Array.from(wrappers).findIndex((w) => w.classList.contains('active'));
    if (activeIdx < 0 && wrapperIdx >= 0) activeIdx = wrapperIdx;
    if (activeIdx < 0 || activeIdx >= wrappers.length) activeIdx = 0;

    tabList.forEach((t) => t.classList.remove('active'));
    wrappers.forEach((w) => w.classList.remove('active'));
    tabList[activeIdx]?.classList.add('active');
    wrappers[activeIdx]?.classList.add('active');
  };

  /** Support 左栏按钮与右栏内容成对同步 */
  const syncSupportDefaults = (entry, panel) => {
    const buttons = entry.querySelectorAll('.ug-m-support-l2');
    const items = panel?.querySelectorAll('.ug-support-menu-item');
    if (!buttons.length || !items?.length) return;

    let activeIdx = Array.from(buttons).findIndex((b) => b.classList.contains('is-active'));
    const itemIdx = Array.from(items).findIndex((i) => i.classList.contains('is-active'));
    if (activeIdx < 0 && itemIdx >= 0) activeIdx = itemIdx;
    if (activeIdx < 0 || activeIdx >= items.length) activeIdx = 0;

    buttons.forEach((b) => b.classList.remove('is-active'));
    items.forEach((i) => i.classList.remove('is-active'));
    buttons[activeIdx]?.classList.add('is-active');
    items[activeIdx]?.classList.add('is-active');
  };

  /** 系列：保证至少一个 L3 面板可见（手机侧 CSS 依赖 .is-active） */
  const syncSeriesDefaults = (entry, panel) => {
    const seriesTargets = panel?.querySelectorAll('[data-series-panel-target]');
    if (!seriesTargets?.length) return;

    if (panel.querySelector('[data-series-panel-target].is-active')) {
      const activeKey = panel.querySelector('[data-series-panel-target].is-active')?.getAttribute('data-series-panel-target');
      const l3 = activeKey ? entry.querySelector(`.ug-mega-menu-series-l3[data-series-panel="${activeKey}"]`) : null;
      if (l3) {
        const l2 = l3.closest('.ug-mega-menu-series-l2');
        entry.querySelectorAll('.ug-mega-menu-series-l2.active').forEach((el) => {
          if (el !== l2) el.classList.remove('active');
        });
        l2?.classList.add('active');
        syncSeriesL3Dots(entry, l3);
      }
      return;
    }

    const firstSeriesL2 =
      entry.querySelector('.ug-mega-menu-series-l2.active') || entry.querySelector('.ug-mega-menu-series-l2');
    if (!firstSeriesL2) {
      seriesTargets[0].classList.add('is-active');
      return;
    }

    entry.querySelectorAll('.ug-mega-menu-series-l2.active').forEach((el) => {
      if (el !== firstSeriesL2) el.classList.remove('active');
    });
    firstSeriesL2.classList.add('active');
    const firstL3 =
      firstSeriesL2.querySelector('.ug-mega-menu-series-l3.active') ||
      firstSeriesL2.querySelector('.ug-mega-menu-series-l3');
    if (firstL3) activateSeriesL3(firstL3);
    else seriesTargets[0].classList.add('is-active');
  };

  /** 右栏默认内容：保证集合 / 系列 / Support 至少有一项可见 */
  const ensurePanelContentVisible = (entry, panel) => {
    if (!(entry instanceof HTMLElement) || !(panel instanceof HTMLElement)) return;
    const type = entry.getAttribute('data-ug-m-type') || '';

    if (type === 'mega') {
      syncCollectionDefaults(entry, panel);
      syncSeriesDefaults(entry, panel);
    }

    if (type === 'support') syncSupportDefaults(entry, panel);
  };

  /** 展开某一 L1；再点只收起左栏，右侧保留 */
  const activateMobileEntry = (entry, { forceOpen = false } = {}) => {
    if (!entry || !isMobile()) return;
    prepareMobileSplitEntries();

    const root = entry.closest('.ug-mobile-drawer__menu') || document;
    const isAlreadyActive = entry.classList.contains('is-active');
    const entryType = entry.getAttribute('data-ug-m-type') || '';

    if (!forceOpen && isAlreadyActive) {
      // 图片 / 链接菜单无 L2，再点不收起，避免右栏被清掉
      if (entryType === 'image' || entryType === 'link') return;
      setMobileEntryActive(entry, false, { syncPanel: false });
      return;
    }

    root.querySelectorAll('[data-ug-m-entry]').forEach((el) => {
      if (el === entry) return;
      setMobileEntryActive(el, false, { syncPanel: true });
    });

    setMobileEntryActive(entry, true, { syncPanel: true });

    const panel = getEntryPanel(entry);
    resetPanelScroll(panel);

    ensurePanelContentVisible(entry, panel);
  };

  const switchMobileCollectionTab = (tab) => {
    if (!tab || !isMobile()) return;
    const entry = tab.closest('[data-ug-m-entry]');
    if (!entry) return;

    if (!entry.classList.contains('is-active')) {
      activateMobileEntry(entry, { forceOpen: true });
    }

    const tabs = entry.querySelectorAll('.ug-mega-menu-left-category-name');
    const panel = getEntryPanel(entry);
    const wrappers = panel?.querySelectorAll('.ug-mega-menu-left-category-list-content-wrapper');
    const index = Array.from(tabs).indexOf(tab);
    if (index === -1) return;

    tabs.forEach((t) => t.classList.remove('active'));
    wrappers?.forEach((w) => w.classList.remove('active'));
    tab.classList.add('active');
    wrappers?.[index]?.classList.add('active');
    resetPanelScroll(panel);
  };

  const measureBoxHeight = (el) => {
    if (!(el instanceof HTMLElement)) return 0;
    const rect = el.getBoundingClientRect();
    return Math.max(el.scrollHeight, el.offsetHeight, Math.ceil(rect.height));
  };

  const markSubmenuReady = (headerMenu) => {
    if (!(headerMenu instanceof HTMLElement)) return;
    headerMenu.setAttribute('data-ug-submenu-ready', '');
  };

  const clearSubmenuReady = (headerMenu) => {
    if (!(headerMenu instanceof HTMLElement)) return;
    if (headerMenu.querySelector('[ref="menuitem"][aria-expanded="true"]')) return;
    headerMenu.removeAttribute('data-ug-submenu-ready');
  };

  const observeSubmenuResize = (submenu) => {
    if (typeof ResizeObserver === 'undefined') return;
    if (!submenuResizeObserver) {
      submenuResizeObserver = new ResizeObserver(() => {
        if (isMobile()) return;
        updateSubmenuHeight({ fromObserver: true });
      });
    }
    submenuResizeObserver.disconnect();
    submenuResizeObserver.observe(submenu);
    submenu.querySelectorAll('.ug-mega-menu-series-l3__panel, .ug-mega-menu-left-category-list-content-wrapper').forEach((el) => {
      submenuResizeObserver.observe(el);
    });
  };

  const syncSeriesDesktopHeight = (scope) => {
    const root = scope instanceof Element ? scope : document;
    const seriesList = root.querySelectorAll('.ug-mega-menu-series');
    if (isMobile()) {
      seriesList.forEach((series) => {
        if (series instanceof HTMLElement) series.style.minHeight = '';
      });
      return;
    }
    seriesList.forEach((series) => {
      if (!(series instanceof HTMLElement)) return;
      const navCol = series.querySelector(':scope > .ug-mega-menu-series__nav-col');
      let panelHeight = 0;
      series.querySelectorAll('.ug-mega-menu-series-l3__panel').forEach((panel) => {
        if (!(panel instanceof HTMLElement)) return;
        if (getComputedStyle(panel).display === 'none') return;
        panelHeight = Math.max(panelHeight, measureBoxHeight(panel));
      });
      const navHeight = navCol instanceof HTMLElement ? measureBoxHeight(navCol) : 0;
      const height = Math.max(navHeight, panelHeight);
      series.style.minHeight = height > 0 ? `${height}px` : '';
    });
  };

  const syncCollectionDesktopHeight = (scope) => {
    const root = scope instanceof Element ? scope : document;
    const menus = root.querySelectorAll('.ug-mega-menu-left');
    if (isMobile()) {
      menus.forEach((menu) => {
        if (menu instanceof HTMLElement) menu.style.minHeight = '';
      });
      return;
    }
    menus.forEach((menu) => {
      if (!(menu instanceof HTMLElement)) return;
      let panelHeight = 0;
      menu.querySelectorAll('.ug-mega-menu-left-category-list-content-wrapper').forEach((panel) => {
        if (!(panel instanceof HTMLElement)) return;
        if (getComputedStyle(panel).display === 'none') return;
        panelHeight = Math.max(panelHeight, measureBoxHeight(panel));
      });
      // 侧栏含绝对定位的右栏，不能用 scrollHeight（会把产品区算进去）
      const sidebar = menu.querySelector('.ug-mega-menu-left__sidebar');
      const leftHeight =
        sidebar instanceof HTMLElement ? Math.ceil(sidebar.getBoundingClientRect().height) : 0;
      const height = Math.max(leftHeight, panelHeight);
      menu.style.minHeight = height > 0 ? `${height}px` : '';
    });
  };

  /**
   * @param {{ fromObserver?: boolean }} [options]
   */
  const updateSubmenuHeight = (options = {}) => {
    if (isMobile()) return;

    const headerMenu = document.querySelector('[header-menu]');
    if (!headerMenu || !(headerMenu instanceof HTMLElement)) return;

    const activeMenuItem = headerMenu.querySelector('[ref="menuitem"][aria-expanded="true"]');
    if (!activeMenuItem) return;

    const menuListItem = activeMenuItem.closest('.menu-list__list-item');
    const activeSubmenu = menuListItem?.querySelector('[ref="submenu[]"]');
    if (!activeSubmenu || !(activeSubmenu instanceof HTMLElement)) return;

    const apply = () => {
      syncSeriesDesktopHeight(activeSubmenu);
      syncCollectionDesktopHeight(activeSubmenu);
      const height = measureBoxHeight(activeSubmenu);
      const current = parseFloat(headerMenu.style.getPropertyValue('--submenu-height')) || 0;
      if (options.fromObserver && Math.abs(height - current) < 2) return;
      headerMenu.style.setProperty('--submenu-height', `${height}px`);
      if (!options.fromObserver) observeSubmenuResize(activeSubmenu);
    };

    apply();
    if (!options.fromObserver) {
      requestAnimationFrame(() => {
        requestAnimationFrame(apply);
      });
    }
  };

  const resetMenuTabState = (menuListItem) => {
    if (!menuListItem || isMobile()) return;

    menuListItem
      .querySelectorAll('.ug-mega-menu-left-category-name.active, .ug-mega-menu-left-category-list-content-wrapper.active')
      .forEach((el) => el.classList.remove('active'));

    menuListItem
      .querySelectorAll('.ug-mega-menu-series-l2.active, .ug-mega-menu-series-l3.active')
      .forEach((el) => el.classList.remove('active'));
  };

  const switchTab = (tab) => {
    if (!tab || isMobile()) return;
    if (tab.classList.contains('active')) return;

    const tabMenuLeft = tab.closest('.ug-mega-menu-left');
    if (!tabMenuLeft) return;

    const tabNames = tabMenuLeft.querySelectorAll('.ug-mega-menu-left-category-name');
    const tabContents = tabMenuLeft.querySelectorAll('.ug-mega-menu-left-category-list-content-wrapper');
    const tabIndex = Array.from(tabNames).indexOf(tab);
    if (tabIndex === -1) return;

    tabNames.forEach((t) => t.classList.remove('active'));
    tabContents.forEach((c) => c.classList.remove('active'));
    tab.classList.add('active');
    tabContents[tabIndex]?.classList.add('active');
    updateSubmenuHeight();
  };

  const activateSeriesL2 = (l2Item) => {
    if (!l2Item || isMobile()) return;
    if (l2Item.classList.contains('active')) return;

    const nav = l2Item.closest('.ug-mega-menu-series__nav');
    if (!nav) return;

    nav.querySelectorAll('.ug-mega-menu-series-l2.active').forEach((item) => {
      if (item !== l2Item) item.classList.remove('active');
    });
    l2Item.classList.add('active');

    const activeChild =
      l2Item.querySelector('.ug-mega-menu-series-l3.active') || l2Item.querySelector('.ug-mega-menu-series-l3');
    if (activeChild) activateSeriesL3(activeChild);
    else updateSubmenuHeight();
  };

  /** 同步 L3 选中态（手机圆点靠 is-on / box-shadow） */
  const syncSeriesL3Dots = (scope, activeL3) => {
    if (!(scope instanceof Element)) return;

    scope.querySelectorAll('.ug-mega-menu-series-l3').forEach((l3) => {
      const on = l3 === activeL3;
      l3.classList.toggle('active', on);
      l3.classList.toggle('is-selected', on);
      const dot = l3.querySelector('.ug-mega-menu-series-l3__dot');
      if (!(dot instanceof HTMLElement)) return;
      dot.classList.toggle('is-on', on);
    });
  };

  const clearTouchHoverState = (scope) => {
    if (!(scope instanceof Element)) return;
    const active = document.activeElement;
    if (active instanceof HTMLElement && scope.contains(active)) active.blur();
    scope.querySelectorAll('.ug-mega-menu-series-l3__nav').forEach((btn) => {
      if (btn instanceof HTMLElement) btn.blur();
    });
  };

  const activateSeriesL3 = (l3Item) => {
    if (!l3Item) return;
    if (!isMobile() && l3Item.classList.contains('active')) return;

    const entry = l3Item.closest('[data-ug-m-entry]');
    // 手机分栏后 nav 已挪出 .ug-mega-menu-series，范围用 entry
    const series = isMobile() && entry ? entry : l3Item.closest('.ug-mega-menu-series') || entry;
    if (!series) return;

    const l2Item = l3Item.closest('.ug-mega-menu-series-l2');
    if (l2Item) {
      const l2Scope = isMobile() ? l2Item.closest('.ug-mega-menu-series__nav') || entry || series : series;
      l2Scope.querySelectorAll('.ug-mega-menu-series-l2.active').forEach((item) => {
        if (item !== l2Item) item.classList.remove('active');
      });
      l2Item.classList.add('active');
    }

    syncSeriesL3Dots(series, l3Item);

    if (isMobile() && entry) {
      const key = l3Item.getAttribute('data-series-panel');
      const panel = getEntryPanel(entry);
      panel?.querySelectorAll('[data-series-panel-target]').forEach((p) => {
        p.classList.toggle('is-active', p.getAttribute('data-series-panel-target') === key);
      });
      resetPanelScroll(panel);
      clearTouchHoverState(entry);
    }

    if (!isMobile()) updateSubmenuHeight();
  };

  /** PC submenu 高度观察：始终绑定一次，内部按断点早退 */
  const initMenuActivationObserver = () => {
    if (desktopObserverBound) return;

    const headerMenu = document.querySelector('[header-menu]');
    if (!headerMenu) return;

    desktopObserverBound = true;
    const menuItems = headerMenu.querySelectorAll('[ref="menuitem"]');
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type !== 'attributes' || mutation.attributeName !== 'aria-expanded') return;
        if (!(mutation.target instanceof Element)) return;

        const menuListItem = mutation.target.closest('.menu-list__list-item');
        const headerMenu = document.querySelector('[header-menu]');
        const expanded = mutation.target.getAttribute('aria-expanded') === 'true';

        if (!expanded) {
          clearSubmenuReady(headerMenu);
          if (menuListItem instanceof HTMLElement) {
            menuListItem.querySelectorAll('.ug-mega-menu-series, .ug-mega-menu-left').forEach((el) => {
              if (el instanceof HTMLElement) el.style.minHeight = '';
            });
          }
          return;
        }

        resetMenuTabState(menuListItem);
        updateSubmenuHeight();
        markSubmenuReady(headerMenu);
      });
    });

    menuItems.forEach((item) => {
      observer.observe(item, {
        attributes: true,
        attributeFilter: ['aria-expanded'],
      });
    });
  };

  /** 系列 L2：再点只收起左栏展开，右侧 L3 保留 */
  const toggleMobileSeriesL2 = (head) => {
    if (!isMobile()) return;

    const l2Item = head.closest('.ug-mega-menu-series-l2');
    if (!l2Item) return;

    const entry = l2Item.closest('[data-ug-m-entry]');
    if (entry && !entry.classList.contains('is-active')) {
      activateMobileEntry(entry, { forceOpen: true });
    }

    const isActive = l2Item.classList.contains('active');
    const nav = l2Item.closest('.ug-mega-menu-series__nav');

    if (isActive) {
      l2Item.classList.remove('active');
      return;
    }

    nav?.querySelectorAll('.ug-mega-menu-series-l2.active').forEach((item) => {
      item.classList.remove('active');
    });
    l2Item.classList.add('active');
    const firstL3 =
      l2Item.querySelector('.ug-mega-menu-series-l3.active') || l2Item.querySelector('.ug-mega-menu-series-l3');
    if (firstL3) activateSeriesL3(firstL3);
  };

  const initEventListeners = () => {
    document.addEventListener('click', (e) => {
      if (!e.target || !(e.target instanceof Element)) return;

      if (!isMobile()) {
        const clickedTab = e.target.closest('.ug-mega-menu-left-category-name');
        if (clickedTab) {
          switchTab(clickedTab);
          return;
        }

        const seriesL2Head = e.target.closest('.ug-mega-menu-series-l2__head');
        if (seriesL2Head) {
          const l2Item = seriesL2Head.closest('.ug-mega-menu-series-l2');
          if (l2Item) activateSeriesL2(l2Item);
          return;
        }

        const seriesL3Nav = e.target.closest('.ug-mega-menu-series-l3__nav');
        if (seriesL3Nav) {
          const l3Item = seriesL3Nav.closest('.ug-mega-menu-series-l3');
          if (l3Item) activateSeriesL3(l3Item);
        }
        return;
      }

      const mobileL1 = e.target.closest('.ug-mobile-drawer__menu [data-ug-m-l1]');
      if (mobileL1) {
        e.preventDefault();
        const entry = mobileL1.closest('[data-ug-m-entry]');
        if (entry) activateMobileEntry(entry);
        return;
      }

      const mobileCollectionTab = e.target.closest('.ug-m-entry__l2 .ug-mega-menu-left-category-name');
      if (mobileCollectionTab) {
        e.preventDefault();
        switchMobileCollectionTab(mobileCollectionTab);
        return;
      }

      const mobileSeriesL2 = e.target.closest('.ug-m-entry__l2 .ug-mega-menu-series-l2__head');
      if (mobileSeriesL2) {
        e.preventDefault();
        toggleMobileSeriesL2(mobileSeriesL2);
        return;
      }

      const mobileSeriesL3 = e.target.closest('.ug-m-entry__l2 .ug-mega-menu-series-l3__nav');
      if (mobileSeriesL3) {
        // 不 preventDefault：iOS 上拦截 click 更容易 sticky :hover
        const l3Item = mobileSeriesL3.closest('.ug-mega-menu-series-l3');
        const entry = mobileSeriesL3.closest('[data-ug-m-entry]');
        if (entry && !entry.classList.contains('is-active')) {
          activateMobileEntry(entry, { forceOpen: true });
        }
        if (l3Item) activateSeriesL3(l3Item);
        return;
      }

      const mobileSupportL2 = e.target.closest('.ug-m-support-l2');
      if (mobileSupportL2 instanceof HTMLElement) {
        e.preventDefault();
        const entry = mobileSupportL2.closest('[data-ug-m-entry]');
        if (entry && !entry.classList.contains('is-active')) {
          activateMobileEntry(entry, { forceOpen: true });
        }
        const idx = mobileSupportL2.dataset.supportIndex;
        const panel = entry ? getEntryPanel(entry) : null;
        entry?.querySelectorAll('.ug-m-support-l2.is-active').forEach((el) => {
          el.classList.remove('is-active');
        });
        panel?.querySelectorAll('.ug-support-menu-item.is-active').forEach((el) => {
          el.classList.remove('is-active');
        });
        mobileSupportL2.classList.add('is-active');
        panel?.querySelector(`.ug-support-menu-item[data-support-index="${idx}"]`)?.classList.add('is-active');
        resetPanelScroll(panel);
      }
    });

    document.addEventListener(
      'mouseenter',
      (e) => {
        if (isMobile() || !e.target || !(e.target instanceof Element)) return;

        const hoveredTab = e.target.closest('.ug-mega-menu-left-category-name');
        if (hoveredTab) {
          switchTab(hoveredTab);
          return;
        }

        const hoveredL2 = e.target.closest('.ug-mega-menu-series-l2__head');
        if (hoveredL2) {
          const l2Item = hoveredL2.closest('.ug-mega-menu-series-l2');
          if (l2Item) activateSeriesL2(l2Item);
          return;
        }

        const hoveredL3 = e.target.closest('.ug-mega-menu-series-l3__nav');
        if (hoveredL3) {
          const l3Item = hoveredL3.closest('.ug-mega-menu-series-l3');
          if (l3Item) activateSeriesL3(l3Item);
        }
      },
      true
    );
  };

  /**
   * @param {{ allowMove?: boolean }} [options] allowMove=false 时打开后锁定位置，避免 iOS 点按重算回闪
   */
  const syncMobileDrawerOffset = ({ allowMove = false } = {}) => {
    if (!isMobile()) return;
    const details = document.querySelector('#Details-menu-drawer-container');
    const root = document.documentElement;
    const detailsOpen =
      details instanceof HTMLDetailsElement && (details.open || details.classList.contains('menu-open'));

    // 已打开就不要再跟 visualViewport / 点按重算，iOS 会把整层抽屉闪一下或跳顶
    if (
      !allowMove &&
      detailsOpen &&
      root.style.getPropertyValue('--ug-mobile-drawer-offset') &&
      root.style.getPropertyValue('--ug-mobile-drawer-height')
    ) {
      return;
    }

    const summary = details?.querySelector(':scope > summary');
    const drawer = details?.querySelector('[data-header-drawer]');

    // 取顶栏可见底边（header 可能是 absolute，#header-group 高度会是 0，不能用来算）
    let top = 0;
    document.querySelectorAll('#header-component, #header-group .header, #header-group .header__row, #header-group .header__columns').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const bottom = el.getBoundingClientRect().bottom;
      if (bottom > top) top = bottom;
    });
    if (top <= 0 && summary instanceof HTMLElement) {
      top = summary.getBoundingClientRect().bottom;
    } else if (top <= 0 && drawer instanceof HTMLElement) {
      top = drawer.getBoundingClientRect().top;
    }

    top = Math.max(0, Math.round(top));
    const vv = window.visualViewport;
    const viewportH =
      vv && typeof vv.height === 'number' && vv.height > 0 ? vv.height : window.innerHeight || 0;
    const height = Math.max(0, Math.round(viewportH - top));
    if (
      root.style.getPropertyValue('--ug-mobile-drawer-offset') === `${top}px` &&
      root.style.getPropertyValue('--ug-mobile-drawer-height') === `${height}px`
    ) {
      return;
    }
    root.style.setProperty('--ug-mobile-drawer-offset', `${top}px`);
    root.style.setProperty('--ug-mobile-drawer-height', `${height}px`);
  };

  /** 打开抽屉默认落到第一个集合/系列菜单（Products），跳过活动图菜单 */
  const getDefaultMobileEntry = () => {
    const entries = Array.from(getDrawerEntries());
    return (
      entries.find((el) => el.getAttribute('data-ug-m-type') === 'mega') ||
      entries.find((el) => el.classList.contains('is-active')) ||
      entries[0] ||
      null
    );
  };

  /**
   * 打开抽屉：始终展开默认产品栏并保证右栏有内容。
   * 抽屉未关时点 L1 收起左栏仍由 activateMobileEntry 自己处理。
   */
  const hydrateMobileDrawer = () => {
    if (!isMobile()) return;

    const drawerDetails = document.querySelector('#Details-menu-drawer-container');
    if (!(drawerDetails instanceof HTMLDetailsElement)) return;
    if (!drawerDetails.open && !drawerDetails.classList.contains('menu-open')) return;

    const defaultEntry = getDefaultMobileEntry();
    if (!(defaultEntry instanceof HTMLElement)) return;
    // 已展开时不要再 forceOpen，避免二级切换被重置
    if (defaultEntry.classList.contains('is-active')) return;

    syncMobileDrawerOffset({ allowMove: true });
    prepareMobileSplitEntries();
    activateMobileEntry(defaultEntry, { forceOpen: true });
  };

  const bindDrawerOpenHooks = () => {
    const drawerDetails = document.querySelector('#Details-menu-drawer-container');
    if (!(drawerDetails instanceof HTMLDetailsElement)) return;

    const scheduleOpenSync = () => {
      if (!drawerDetails.open && !drawerDetails.classList.contains('menu-open')) return;
      if (openSyncScheduled) return;
      openSyncScheduled = true;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          openSyncScheduled = false;
          if (!drawerDetails.open && !drawerDetails.classList.contains('menu-open')) return;
          hydrateMobileDrawer();
        });
      });
    };

    drawerDetails.addEventListener(
      'click',
      (e) => {
        if (!(e.target instanceof Element)) return;
        const summary = drawerDetails.querySelector(':scope > summary');
        if (summary && (e.target === summary || summary.contains(e.target))) return;
        if (e.target.closest('a[href], input, select, textarea')) return;
        e.preventDefault();
      },
      true
    );

    window.addEventListener('toggle', (e) => keepMobileDrawerOpen(e.target), true);

    drawerDetails.addEventListener('toggle', () => {
      if (keepMobileDrawerOpen(drawerDetails)) return;
      if (drawerDetails.open) scheduleOpenSync();
    });

    let wasMenuOpen = drawerDetails.classList.contains('menu-open');
    const classObserver = new MutationObserver(() => {
      const isOpen = drawerDetails.classList.contains('menu-open');
      if (isOpen && !wasMenuOpen) scheduleOpenSync();
      wasMenuOpen = isOpen;
    });
    classObserver.observe(drawerDetails, { attributes: true, attributeFilter: ['class'] });

    window.addEventListener('orientationchange', () => {
      syncMobileDrawerOffset({ allowMove: true });
    });

    // 脚本晚于用户点开：补一次 hydrate，避免右栏空白
    if (drawerDetails.open || drawerDetails.classList.contains('menu-open')) {
      scheduleOpenSync();
    }
  };

  const init = () => {
    prepareMobileSplitEntries();
    syncMobileDrawerOffset();
    initEventListeners();
    initMenuActivationObserver();
    // 抽屉未打开时不 hydrate，避免首屏强行激活 L1
    bindDrawerOpenHooks();

    window.addEventListener('resize', () => {
      if (isMobile()) {
        syncSeriesDesktopHeight(document);
        syncCollectionDesktopHeight(document);
        syncMobileDrawerOffset();
        prepareMobileSplitEntries();
      } else {
        initMenuActivationObserver();
        updateSubmenuHeight();
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
